import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { createDefaultATier, createDefaultBTier, createDefaultCTier, createDefaultDTier, createDefaultETier, createDefaultFTier, createDefaultSTier, TierCosmetic, createDefaultExcellentUniquesTier, createDefaultGoodUniquesTier, createDefaultPotentialUniquesTier, createDefaultOtherUniquesTier, createDefaultSearchingChanceBasesTier, createDefaultPotentialChanceBasesTier, Filter, CosmeticOptions, createDefaultRestrictedUniquesTier } from '../../models/filter-models';
import { Color, Comparator, LabelSize, MinimapIconShape, MinimapIconSize, Rarity } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { Tier } from '../../models/item-data-models';
import { CosmeticControls } from '../cosmetic-controls.model';
import { CosmeticControlsComponent } from '../cosmetic-controls/cosmetic-controls.component';
import { CosmeticSettingsComponent } from '../cosmetic-settings/cosmetic-settings.component';
import { CurrencyTierService } from '../../services/tiering/currency-tier.service';
import { UniquesTierService } from '../../services/tiering/uniques-tier.service';
import { BaseComponent } from '../../common/base.component';
import { CosmeticStyleOptionsMenuComponent } from '../../common/cosmetic-style-options-menu/cosmetic-style-options-menu.component';
import { CosmeticStyleClipboardService } from '../../services/cosmetic-style-clipboard.service';

@Component({
  selector: 'app-tier-cosmetics',
  imports: [FormsModule, CosmeticControlsComponent, CosmeticSettingsComponent, CosmeticStyleOptionsMenuComponent],
  templateUrl: './tier-cosmetics.component.html',
  styleUrl: './tier-cosmetics.component.scss'
})
export class TierCosmeticsComponent extends BaseComponent implements OnInit {
  @Input({ required: true }) tier!: TierCosmetic;
  @Input({ required: true }) filter!: Filter;
  @Input() disabled?: boolean;
  @Input() tierLevel?: Tier;;
  @Input() isFirst?: boolean;
  @Input() isLast?: boolean;
  @Input() divCard?: boolean;
  @Input() equipment?: boolean;

  @Output('updateFilter') updateFilterEmitter = new EventEmitter<void>();

  @Output('removeTier') removeTierEmitter = new EventEmitter<TierCosmetic>();
  @Output('cloneTier') cloneTierEmitter = new EventEmitter<TierCosmetic>();
  @Output('changeTierName') changeTierNameEmitter = new EventEmitter<{ oldName: string, newName: string }>();

  cosmeticControls = new CosmeticControls();

  Tier = Tier;
  Rarity = Rarity;
  Comparator = Comparator;
  MinimapIconShape = MinimapIconShape;
  MinimapIconSize = MinimapIconSize;
  Color = Color;
  LabelSize = LabelSize;

  oldTierName: string = '';

  defaultTierCutoffValues?: Record<Tier, { priceLabel: string, priceBaseCurrency: string, priceBaseCurrencyImage?: string }>;
  defaultUniqueTierCutoffValues?: Record<string, { priceLabel: string, priceBaseCurrency?: string, priceBaseCurrencyImage?: string }>;

  active = true;
  dirtyForceUpdate = 0;

  private readonly styleClipboard = inject(CosmeticStyleClipboardService);

  constructor(private currencyTierService: CurrencyTierService, private uniquesTierService: UniquesTierService) {
    super();
  }

  ngOnInit(): void {
    this.updateActive();

    this.defaultTierCutoffValues = this.currencyTierService.getSelectedTierValueList().reduce((acc, curr) => {
      acc[curr.tier] = this.currencyTierService.getDisplayValue(curr.tier);
      return acc;
    }, {} as Record<Tier, { priceLabel: string, priceBaseCurrency: string, priceBaseCurrencyImage?: string }>);

    this.defaultUniqueTierCutoffValues = this.uniquesTierService.getSelectedTierCutoffValues().reduce((acc, curr) => {
      acc[curr.tier] = this.uniquesTierService.getDisplayValue(curr.tier);
      return acc;
    }, {} as Record<string, { priceLabel: string, priceBaseCurrency?: string, priceBaseCurrencyImage?: string }>);
  }

  updateActive() {
    this.active = this.tier.displayOptions.active || false;
  }

  removeTier = () => this.removeTierEmitter.emit(this.tier);
  cloneTier = () => this.cloneTierEmitter.emit(this.tier);

  changeTierName(event: Event) {
    const newName = (event.target as HTMLInputElement).value;
    this.changeTierNameEmitter.emit({ oldName: this.oldTierName, newName });
    this.updateFilter();
  }

  storeOldTierName(event: FocusEvent) {
    this.oldTierName = (event.target as HTMLInputElement).value;
  }

  resetToDefaults() {
    if (this.tierLevel) {
      const defaults = this.tierLevel === Tier.SPurple ? createDefaultSTier()
        : this.tierLevel === Tier.ARed ? createDefaultATier()
        : this.tierLevel === Tier.BOrange ? createDefaultBTier()
        : this.tierLevel === Tier.CYellow ? createDefaultCTier()
        : this.tierLevel === Tier.DWhite ? createDefaultDTier()
        : this.tierLevel === Tier.EVisible ? createDefaultETier()
        : this.tierLevel === Tier.FHidden ? createDefaultFTier()
        : createDefaultETier();

      Object.assign(this.tier, defaults);
      this.updateFilter();
    } else if (this.tier.uniqueTier) {
      const defaults = this.tier.uniqueTier === 'Excellent' ? createDefaultExcellentUniquesTier()
        : this.tier.uniqueTier === 'Good' ? createDefaultGoodUniquesTier()
        : this.tier.uniqueTier === 'Restricted' ? createDefaultRestrictedUniquesTier()
        : this.tier.uniqueTier === 'Potential' ? createDefaultPotentialUniquesTier()
        : createDefaultOtherUniquesTier();

      Object.assign(this.tier, defaults);
      this.updateFilter();
    } else if (this.tier.chanceBaseTier) {
      const defaults = this.tier.chanceBaseTier === 'Searching' ? createDefaultSearchingChanceBasesTier()
        : createDefaultPotentialChanceBasesTier();

      Object.assign(this.tier, defaults);
      this.updateFilter();
    }
  }

  toggleTierActive() {
    this.tier.displayOptions.active = !this.tier.displayOptions.active;
    if (this.tier.uniqueTier) {
      this.disableHideAllRemainingWhenAllUniquesAreEnabled();
    }
    this.updateFilter();
    this.updateActive();
  }

  disableHideAllRemainingWhenAllUniquesAreEnabled = () => {
    if (this.filter.uniqueTierCosmetics[0].displayOptions.active
      && this.filter.uniqueTierCosmetics[1].displayOptions.active
      && this.filter.uniqueTierCosmetics[2].displayOptions.active
      && this.filter.uniqueTierCosmetics[3].displayOptions.active
      && this.filter.uniqueTierCosmetics[4].displayOptions.active) {
      this.filter.hideAllRemainingUniques = false;
    }
  }

  updateFilter() {
    this.dirtyForceUpdate++;
    this.updateFilterEmitter.emit();
  }

  selectAllText(event: FocusEvent) {
    (event.target as HTMLInputElement).select();
  }

  getTierCopyOptions = () => this.tier.displayOptions.cosmeticOptions!;

  getTierCopyNameHint = () => {
    if (this.tier.uniqueTier) return this.tier.uniqueTier;
    if (this.tier.chanceBaseTier) {
      return this.tier.chanceBaseTier === 'Searching' ? 'Favourite' : this.tier.chanceBaseTier;
    }
    if (this.tier.name) return this.tier.name;
    if (this.tier.tier) return `${this.tier.tier}-Tier`;
    return undefined;
  };

  pasteStyle = (options: CosmeticOptions) => {
    if (!this.tier.displayOptions.cosmeticOptions) {
      this.tier.displayOptions.cosmeticOptions = new CosmeticOptions();
    }
    this.styleClipboard.pasteTo(this.tier.displayOptions.cosmeticOptions, options);
    this.updateFilter();
  };
}
